import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import * as Sentry from '@sentry/nuxt'
import { getApiErrorMessage } from '~/utils/getApiErrorMessage'
import { isCharacterIndependentSlot } from '~/utils/cosmetics'

// Types
export type CosmeticSlot =
  | 'head' | 'torso' | 'legs' | 'shoes' | 'pet' | 'background'

export type CosmeticRarity =
  | 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' | 'mythic'

// A character slug, e.g. 'jack', 'ruby', 'skeleton'. Matches characters.id in
// the DB and the GameCharacter id used by the Spine renderer.
export type CharacterId = string

export interface EquippedCosmetic {
  id: string
  assetUrl: string
  name: string
  slug: string
}

export interface EquippedCosmetics {
  head: EquippedCosmetic | null
  torso: EquippedCosmetic | null
  legs: EquippedCosmetic | null
  shoes: EquippedCosmetic | null
  pet: EquippedCosmetic | null
  background: EquippedCosmetic | null
}

export interface PlayerEquipped {
  userId: string
  characterId: CharacterId
  displayName: string | null
  equipped: EquippedCosmetics
}

export interface OwnedCosmetic {
  id: string
  cosmeticId: string
  name: string
  slug: string
  description: string
  slot: CosmeticSlot
  rarity: CosmeticRarity
  characters: CharacterId[]
  assetUrl: string
  thumbnailUrl: string
  acquiredAt: string
  source: 'purchase' | 'season' | 'airdrop' | 'reward'
  themeSet?: string | null
}

export interface ShopItem {
  id: string
  slug: string
  name: string
  description: string
  slot: CosmeticSlot
  rarity: CosmeticRarity
  characters: CharacterId[]
  assetUrl: string
  thumbnailUrl: string
  // Theme-set slug this item belongs to (null for non-set items). Used to group
  // shop items into the Sets section.
  themeSet?: string | null
  priceXaut: string | null
  priceUsd: string | null
  isFeatured: boolean
  isNew: boolean
  releasedAt: string
}

export interface ShopBundle {
  id: string
  name: string
  description: string
  items: ShopItem[]
  priceXaut: string | null
  priceUsd: string | null
  discountPercent: number
}

export interface ShopCategory {
  slot: CosmeticSlot
  items: ShopItem[]
}

export interface ShopResponse {
  featured: ShopItem[]
  newArrivals: ShopItem[]
  bundles: ShopBundle[]
  categories: ShopCategory[]
}

export interface ThemeSet {
  id: string
  slug: string
  name: string
  description: string | null
  requiredCount: number
  bonusType: 'visual' | 'xp_boost' | 'both'
  bonusVisual: string | null
  bonusXpPercent: number
  iconUrl: string | null
  color: string | null
  // Base path for the set preview image; the frontend appends _<characterId>
  // before the extension for per-character variants. NULL until art is provided.
  previewUrl: string | null
  // Character ids this set serves, i.e. it has a shop-available item compatible
  // with that character. Used to count "more sets available" for the selected
  // character. Empty means the set has no shop-available item for anyone (e.g.
  // unreleased) — such sets are NOT counted as available.
  characters: CharacterId[]
}

// Unlock progress for a single PRO (unlock-gated) character, as returned by the
// GET /character-unlock-progress endpoint. Deposit/wager amounts and thresholds are
// USD strings (the player's XAUT aggregates converted at the current rate); priceUsd
// is the buy price in USD. Thresholds are null for non-PRO characters.
export interface CharacterUnlockProgress {
  characterId: CharacterId
  isUnlocked: boolean
  priceUsd: string | null
  priceXaut: string | null
  balance: string
  depositedUsd: string
  depositThresholdUsd: string | null
  wageredUsd: string
  wagerThresholdUsd: string | null
}

// Constants
export const COSMETIC_SLOTS: CosmeticSlot[] = [
  'head', 'torso', 'legs', 'shoes', 'pet', 'background'
]

// Default equipped cosmetics (all null)
const DEFAULT_EQUIPPED: EquippedCosmetics = {
  head: null,
  torso: null,
  legs: null,
  shoes: null,
  pet: null,
  background: null,
}

export const useCharacterStore = defineStore('character', () => {
  const config = useRuntimeConfig()
  const { getAccessToken } = useSupabaseAuth()

  // State
  const playerEquipped = ref<PlayerEquipped | null>(null)
  const inventory = ref<OwnedCosmetic[]>([])
  const shop = ref<ShopResponse | null>(null)
  const isLoadingInventory = ref(false)
  const isLoadingShop = ref(false)
  const isPurchasing = ref(false)
  const shopCounts = ref<Record<CosmeticSlot, number>>({ head: 0, torso: 0, legs: 0, shoes: 0, pet: 0, background: 0 })
  const themeSets = ref<ThemeSet[]>([])
  const isLoadingSets = ref(false)
  // Unlock progress per PRO character id (keyed by characters.id, e.g. 'adrienne').
  const proUnlock = ref<Record<CharacterId, CharacterUnlockProgress>>({})
  const error = ref<string | null>(null)

  // Aggregate loading flag (for callers that don't care which op is in flight)
  // isLoadingSets is intentionally excluded — the Outfit tab owns its own sets loading state.
  const isLoading = computed(
    () => isLoadingInventory.value || isLoadingShop.value || isPurchasing.value
  )

  // Getters
  const characterId = computed((): CharacterId => {
    return playerEquipped.value?.characterId ?? 'jack'
  })

  const equipped = computed((): EquippedCosmetics => {
    return playerEquipped.value?.equipped ?? { ...DEFAULT_EQUIPPED }
  })

  const ownedItems = computed((): OwnedCosmetic[] => {
    return inventory.value
  })

  const itemsBySlot = computed((): Record<CosmeticSlot, OwnedCosmetic[]> => {
    const grouped: Record<CosmeticSlot, OwnedCosmetic[]> = {
      head: [],
      torso: [],
      legs: [],
      shoes: [],
      pet: [],
      background: [],
    }

    for (const item of inventory.value) {
      if (grouped[item.slot]) {
        grouped[item.slot].push(item)
      }
    }

    return grouped
  })

  const ownedIds = computed((): Set<string> => {
    return new Set(inventory.value.map(item => item.cosmeticId))
  })

  const featuredItems = computed((): ShopItem[] => {
    return shop.value?.featured ?? []
  })

  const newArrivals = computed((): ShopItem[] => {
    return shop.value?.newArrivals ?? []
  })

  const shopCategories = computed((): ShopCategory[] => {
    return shop.value?.categories ?? []
  })

  // A theme set is "owned" for the current character when the player owns at
  // least requiredCount of its items that are compatible with that character.
  const ownedSets = computed((): ThemeSet[] => {
    const char = characterId.value
    return themeSets.value.filter((set) => {
      const owned = inventory.value.filter(
        (i) => i.themeSet === set.slug
          && (isCharacterIndependentSlot(i.slot) || (i.characters?.includes(char) ?? false)),
      )
      return owned.length >= set.requiredCount
    })
  })

  // cosmetic id → theme-set slug, for items belonging to an owned set (drives
  // the "Equip all set items?" popover).
  const setMembership = computed((): Map<string, string> => {
    const ownedSlugs = new Set(ownedSets.value.map((s) => s.slug))
    const map = new Map<string, string>()
    for (const item of inventory.value) {
      if (item.themeSet && ownedSlugs.has(item.themeSet) && !map.has(item.cosmeticId)) {
        map.set(item.cosmeticId, item.themeSet)
      }
    }
    return map
  })

  // Helper to get access token
  async function getAuthHeaders(): Promise<Record<string, string>> {
    const accessToken = await getAccessToken()
    if (!accessToken) {
      throw new Error('Not authenticated')
    }
    const sessionId = typeof window !== 'undefined'
      ? localStorage.getItem('aurumrun_session_id')
      : null
    return {
      Authorization: `Bearer ${accessToken}`,
      ...(sessionId && { 'X-Session-Id': sessionId }),
    }
  }

  // Actions
  async function fetchInventory(): Promise<void> {
    isLoadingInventory.value = true
    error.value = null

    try {
      const headers = await getAuthHeaders()
      const response = await $fetch<{
        owned: OwnedCosmetic[]
        equipped: PlayerEquipped
        shopCounts: Record<CosmeticSlot, number>
      }>(`${config.public.supabaseUrl}/functions/v1/cosmetics-inventory`, {
        headers,
      })

      inventory.value = response.owned
      playerEquipped.value = response.equipped
      shopCounts.value = response.shopCounts
    } catch (err) {
      console.error('[characterStore] Failed to fetch inventory:', err)
      Sentry.captureException(err, { tags: { store: 'character', action: 'fetchInventory' } })
      error.value = getApiErrorMessage(err, 'Failed to fetch inventory')
      throw err
    } finally {
      isLoadingInventory.value = false
    }
  }

  // Refresh only the shop "+N more" counts. These are per active character on the
  // backend, so they go stale when the active character changes — but a full
  // fetchInventory() would flip isLoadingInventory and flash the inventory
  // skeleton, undoing the optimistic instant switch. This refetches the same
  // endpoint and updates only shopCounts (owned/equipped left untouched), and
  // never throws — stale counts must not break a character switch.
  async function refreshShopCounts(): Promise<void> {
    try {
      const headers = await getAuthHeaders()
      const response = await $fetch<{
        shopCounts: Record<CosmeticSlot, number>
      }>(`${config.public.supabaseUrl}/functions/v1/cosmetics-inventory`, {
        headers,
      })
      shopCounts.value = response.shopCounts
    } catch (err) {
      console.error('[characterStore] Failed to refresh shop counts:', err)
      Sentry.captureException(err, { tags: { store: 'character', action: 'refreshShopCounts' } })
    }
  }

  // Monotonic token to drop out-of-order shop responses. When the user switches
  // characters quickly, an earlier request can resolve after a later one; without
  // this guard the stale response would overwrite the newer catalog (and clear
  // the loading flag early), causing the Sets/skins to flicker. Only the latest
  // request applies its result and toggles isLoadingShop.
  let shopRequestToken = 0
  async function fetchShop(character?: CharacterId): Promise<void> {
    const token = ++shopRequestToken
    isLoadingShop.value = true
    error.value = null

    try {
      const headers = await getAuthHeaders()
      // Pass the character so the Shop tab can browse a character other than the
      // player's active one and still get that character's full catalog (sets +
      // skins). Without it the backend defaults to the player's character. #716
      const response = await $fetch<ShopResponse>(
        `${config.public.supabaseUrl}/functions/v1/cosmetics-shop`,
        { headers, query: character ? { character } : undefined }
      )

      // A newer fetchShop superseded this one — discard the stale catalog.
      if (token !== shopRequestToken) return
      shop.value = response
    } catch (err) {
      console.error('[characterStore] Failed to fetch shop:', err)
      Sentry.captureException(err, { tags: { store: 'character', action: 'fetchShop' } })
      // Don't surface or rethrow a superseded request's error — the newer one owns the UI.
      if (token !== shopRequestToken) return
      error.value = getApiErrorMessage(err, 'Failed to fetch shop')
      throw err
    } finally {
      // Only the latest request controls the loading flag, so it stays true until
      // the newest catalog actually arrives.
      if (token === shopRequestToken) isLoadingShop.value = false
    }
  }

  async function fetchSets(): Promise<void> {
    isLoadingSets.value = true
    error.value = null
    try {
      const headers = await getAuthHeaders()
      const response = await $fetch<{ themeSets: ThemeSet[] }>(
        `${config.public.supabaseUrl}/functions/v1/cosmetics-theme-sets`,
        { headers },
      )
      themeSets.value = response.themeSets ?? []
    } catch (err) {
      console.error('[characterStore] Failed to fetch theme sets:', err)
      Sentry.captureException(err, { tags: { store: 'character', action: 'fetchSets' } })
      error.value = getApiErrorMessage(err, 'Failed to fetch sets')
      throw err
    } finally {
      isLoadingSets.value = false
    }
  }

  // Network-only equip POST — no optimistic UI update, no rollback. Callers own
  // the optimistic state so they can batch several slots into one visual update.
  async function persistEquip(
    slot: CosmeticSlot,
    cosmeticId: string | null,
    characters?: CharacterId[],
  ): Promise<void> {
    const headers = await getAuthHeaders()
    await $fetch(`${config.public.supabaseUrl}/functions/v1/cosmetics-equip`, {
      method: 'POST',
      headers,
      // Only send `characters` when targeting an explicit set, so the default
      // outfit-tab equip keeps the minimal `{ slot, cosmeticId }` body.
      body: characters === undefined
        ? { slot, cosmeticId }
        : { slot, cosmeticId, characters },
    })
  }

  // Equips every owned, character-compatible item of a theme set. All slots are
  // applied to the preview in ONE optimistic update so the outfit changes in a
  // single step (not item-by-item as each request resolves), then persisted.
  async function equipSet(slug: string): Promise<void> {
    if (!playerEquipped.value) {
      throw new Error('Player data not loaded')
    }
    const char = characterId.value
    const bySlot = new Map<CosmeticSlot, OwnedCosmetic>()
    for (const item of inventory.value) {
      if (item.themeSet !== slug) continue
      const compatible = isCharacterIndependentSlot(item.slot) || (item.characters?.includes(char) ?? false)
      if (!compatible) continue
      if (!bySlot.has(item.slot)) bySlot.set(item.slot, item)
    }
    if (bySlot.size === 0) return

    // Optimistically apply every slot at once — a single synchronous batch, so
    // the skin watcher fires once and the whole set appears together.
    const previousEquipped = { ...playerEquipped.value.equipped }
    for (const [slot, item] of bySlot) {
      playerEquipped.value.equipped[slot] = {
        id: item.cosmeticId,
        assetUrl: item.assetUrl,
        name: item.name,
        slug: item.slug,
      }
    }

    // Persist each slot (network only — the preview is already updated). A slot
    // whose request fails is reverted to its pre-batch value so the preview
    // never claims an item that didn't actually equip.
    const failures: unknown[] = []
    for (const [slot, item] of bySlot) {
      try {
        await persistEquip(slot, item.cosmeticId)
      } catch (err) {
        failures.push(err)
        if (playerEquipped.value) {
          playerEquipped.value.equipped[slot] = previousEquipped[slot]
        }
      }
    }

    // Rethrow the first failure; subsequent ones are discarded (the caller shows
    // one toast per action).
    if (failures.length > 0) {
      error.value = getApiErrorMessage(failures[0], 'Failed to equip set')
      throw failures[0]
    }
  }

  /**
   * Equip/unequip a cosmetic.
   *
   * `characters` targets a specific set of characters (non-pet only). When
   * omitted the backend equips the selected character — the outfit-tab behavior.
   * The shop "Dress" flow (see {@link dressItem}) passes every character a skin
   * fits so a unisex skin is worn by both characters. The visible avatar is only
   * updated optimistically when the change affects the selected character.
   */
  async function equipItem(
    slot: CosmeticSlot,
    cosmeticId: string | null,
    characters?: CharacterId[],
  ): Promise<void> {
    if (!playerEquipped.value) {
      throw new Error('Player data not loaded')
    }

    // Store previous state for rollback
    const previousEquipped = { ...playerEquipped.value.equipped }

    // Only touch the visible avatar when the change affects the selected
    // character (no `characters` = selected character; or the set includes it).
    const affectsSelected = characters === undefined
      || characters.includes(characterId.value)

    // Optimistic update
    if (affectsSelected) {
      if (cosmeticId === null) {
        // Unequip item
        playerEquipped.value.equipped[slot] = null
      } else {
        // Find the item in inventory
        const item = inventory.value.find(i => i.cosmeticId === cosmeticId)
        if (item) {
          playerEquipped.value.equipped[slot] = {
            id: item.cosmeticId,
            assetUrl: item.assetUrl,
            name: item.name,
            slug: item.slug,
          }
        }
      }
    }

    try {
      await persistEquip(slot, cosmeticId, characters)
    } catch (err) {
      // Rollback on error
      console.error('[characterStore] Failed to equip item:', err)
      Sentry.captureException(err, { tags: { store: 'character', action: 'equipItem' } })
      playerEquipped.value.equipped = previousEquipped
      error.value = getApiErrorMessage(err, 'Failed to equip item')
      throw err
    }
  }

  /**
   * Dress an owned cosmetic onto EVERY character it fits (shop "Buy & Dress").
   * A unisex skin lands on both characters; a character-specific skin on its one
   * character. Pets are shared, so they dress via the normal pet path.
   */
  async function dressItem(cosmeticId: string): Promise<void> {
    // The item may live in inventory (just purchased) or the shop catalog.
    const owned = inventory.value.find(i => i.cosmeticId === cosmeticId)
    const shopItem = shop.value?.categories.flatMap(c => c.items).find(i => i.id === cosmeticId)
      ?? shop.value?.featured.find(i => i.id === cosmeticId)
    const slot = owned?.slot ?? shopItem?.slot
    const characters = owned?.characters ?? shopItem?.characters
    if (!slot) {
      throw new Error('Item not found for dressing')
    }

    if (isCharacterIndependentSlot(slot)) {
      // Pets and backgrounds are shared across characters — no per-character targeting.
      await equipItem(slot, cosmeticId)
      return
    }

    await equipItem(slot, cosmeticId, characters ?? [])
  }

  async function updateCharacter(newCharacterId: CharacterId): Promise<void> {
    if (!playerEquipped.value) {
      throw new Error('Player data not loaded')
    }

    try {
      const headers = await getAuthHeaders()
      const response = await $fetch<{
        success: boolean
        equipped: PlayerEquipped
      }>(`${config.public.supabaseUrl}/functions/v1/cosmetics-update-character`, {
        method: 'POST',
        headers,
        body: { characterId: newCharacterId },
      })

      // Apply the new character AND its saved outfit in the SAME reactive update.
      // Each character now has its own outfit, so we can't optimistically swap
      // the character ahead of the response — doing so would briefly show the new
      // character wearing the previous character's outfit, then re-skin once the
      // real outfit arrives (the "staged" preview update). Setting both together
      // lets the viewer reload the skeleton and apply skins in one step.
      if (response.success && response.equipped) {
        playerEquipped.value.characterId = newCharacterId
        playerEquipped.value.equipped = response.equipped.equipped
      }

      // The switch persisted server-side, so the shop "+N more" counts (filtered
      // to the active character) are now stale — refresh them silently.
      await refreshShopCounts()
    } catch (err) {
      // Nothing was mutated before the response, so there's no state to roll back.
      console.error('[characterStore] Failed to update character:', err)
      Sentry.captureException(err, { tags: { store: 'character', action: 'updateCharacter' } })
      error.value = getApiErrorMessage(err, 'Failed to update character')
      throw err
    }
  }

  // `equip` (default true) asks the backend to wear the cosmetic immediately
  // after purchase — best-effort, only if compatible with the active character.
  // The freshly-equipped state is reflected by the fetchInventory() below. #721
  async function purchaseItem(cosmeticId: string, equip = true): Promise<void> {
    isPurchasing.value = true
    error.value = null

    try {
      const headers = await getAuthHeaders()
      await $fetch(`${config.public.supabaseUrl}/functions/v1/cosmetics-purchase`, {
        method: 'POST',
        headers,
        body: {
          cosmeticId,
          paymentMethod: 'xaut',
          equip,
        },
      })

      // Refetch inventory to get the newly purchased item
      await fetchInventory()
    } catch (err) {
      console.error('[characterStore] Failed to purchase item:', err)
      Sentry.captureException(err, { tags: { store: 'character', action: 'purchaseItem' } })
      error.value = getApiErrorMessage(err, 'Failed to purchase item')
      throw err
    } finally {
      isPurchasing.value = false
    }
  }

  // Buys several cosmetics (a set) by looping the existing /cosmetics-purchase —
  // there is no atomic set-purchase endpoint. Sequential so the balance debits
  // in order; inventory is refetched once at the end. Rethrows the first failure
  // (already-bought items still land; the caller surfaces one error).
  // `equip` (default true) auto-wears each set item server-side as it's bought
  // (best-effort, active-character only). Since the loop awaits every purchase
  // and fetchInventory() runs at the end, the modal stays in its loading state
  // until all items are bought and equipped. #721
  async function purchaseSet(cosmeticIds: string[], equip = true): Promise<void> {
    // Skip ids already owned (e.g. acquired since the set modal opened) so a
    // redundant double-purchase can't turn the whole set purchase into a
    // misleading failure. Nothing left to buy → no-op.
    const toBuy = cosmeticIds.filter((id) => !isOwned(id))
    if (toBuy.length === 0) return
    isPurchasing.value = true
    error.value = null
    const failures: unknown[] = []
    try {
      const headers = await getAuthHeaders()
      for (const cosmeticId of toBuy) {
        try {
          await $fetch(`${config.public.supabaseUrl}/functions/v1/cosmetics-purchase`, {
            method: 'POST',
            headers,
            body: { cosmeticId, paymentMethod: 'xaut', equip },
          })
        } catch (err) {
          failures.push(err)
        }
      }
      await fetchInventory()
      if (failures.length > 0) throw failures[0]
    } catch (err) {
      console.error('[characterStore] Failed to purchase set:', err)
      Sentry.captureException(err, { tags: { store: 'character', action: 'purchaseSet' } })
      error.value = getApiErrorMessage(err, 'Failed to purchase set')
      throw err
    } finally {
      isPurchasing.value = false
    }
  }

  function isOwned(cosmeticId: string): boolean {
    return ownedIds.value.has(cosmeticId)
  }

  // True once we know a PRO character is owned/unlocked. Defaults to false until
  // progress is fetched, so a locked Pro pill stays gated (opens the unlock modal)
  // rather than switching prematurely. Only meaningful for PRO characters — plain
  // characters never go through this check.
  function isCharacterUnlocked(id: CharacterId): boolean {
    return proUnlock.value[id]?.isUnlocked ?? false
  }

  // Fetches unlock progress for a single PRO character. Server-side this also
  // lazily grants the character if a wager threshold is now met (fire-and-forget
  // on the backend), so calling it on page load / modal open doubles as the
  // wager-unlock trigger. Non-throwing: a failed fetch must not break the page.
  async function fetchCharacterUnlock(id: CharacterId): Promise<CharacterUnlockProgress | null> {
    try {
      const headers = await getAuthHeaders()
      const response = await $fetch<CharacterUnlockProgress>(
        `${config.public.supabaseUrl}/functions/v1/character-unlock-progress`,
        { headers, query: { characterId: id } },
      )
      proUnlock.value = { ...proUnlock.value, [id]: response }
      return response
    } catch (err) {
      console.error('[characterStore] Failed to fetch character unlock progress:', err)
      Sentry.captureException(err, { tags: { store: 'character', action: 'fetchCharacterUnlock' } })
      return null
    }
  }

  // Buys a PRO character outright (debits XAUT for the USD price). On success the
  // backend grants ownership; we refetch progress so isUnlocked/balance reflect it.
  async function purchaseCharacter(id: CharacterId): Promise<void> {
    isPurchasing.value = true
    error.value = null
    try {
      const headers = await getAuthHeaders()
      await $fetch<{ success: boolean }>(
        `${config.public.supabaseUrl}/functions/v1/character-unlock-purchase`,
        { method: 'POST', headers, body: { characterId: id } },
      )
      // Refresh authoritative state (isUnlocked, balance) after the grant.
      await fetchCharacterUnlock(id)
    } catch (err) {
      console.error('[characterStore] Failed to purchase character:', err)
      Sentry.captureException(err, { tags: { store: 'character', action: 'purchaseCharacter' } })
      error.value = getApiErrorMessage(err, 'Failed to purchase character')
      throw err
    } finally {
      isPurchasing.value = false
    }
  }

  function setDisplayName(name: string): void {
    if (playerEquipped.value) {
      playerEquipped.value.displayName = name
    }
  }

  function reset(): void {
    playerEquipped.value = null
    inventory.value = []
    shop.value = null
    // Invalidate any in-flight fetchShop so a late response can't write into the
    // just-reset store (reset runs on logout/teardown).
    shopRequestToken++
    isLoadingInventory.value = false
    isLoadingShop.value = false
    isPurchasing.value = false
    themeSets.value = []
    isLoadingSets.value = false
    proUnlock.value = {}
    error.value = null
  }

  return {
    // State
    playerEquipped,
    inventory,
    shop,
    shopCounts,
    themeSets,
    proUnlock,
    isLoadingInventory,
    isLoadingShop,
    isLoadingSets,
    isPurchasing,
    isLoading,
    error,
    // Getters
    characterId,
    equipped,
    ownedItems,
    itemsBySlot,
    ownedIds,
    featuredItems,
    newArrivals,
    shopCategories,
    ownedSets,
    setMembership,
    // Actions
    fetchInventory,
    refreshShopCounts,
    fetchShop,
    fetchSets,
    equipSet,
    equipItem,
    dressItem,
    updateCharacter,
    purchaseItem,
    purchaseSet,
    isOwned,
    isCharacterUnlocked,
    fetchCharacterUnlock,
    purchaseCharacter,
    setDisplayName,
    reset,
  }
})
